import { FC } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Link,
} from '@chakra-ui/react';

import { CompanyLegalBeneficiaryOnboardingStatus } from '../../../../__generated__/GQL';
import Callout from '../../../../components/_core/Callout';
import SunshineIcon from '../../../../components/_core/SunshineIcon';
import Text from '../../../../components/_core/Text';
import BeneficiaryCard from './components/BeneficiaryCard';
import BeneficiarySection from './components/BeneficiarySection';
import CreateBeneficiary from './components/CreateBeneficiary';
import locales from './locales';
import { Beneficiary } from './types';

export type BeneficiariesFollowupProps = {
  accountHolderEmail: string;
  beneficiaries: Beneficiary[];
  isAddingNewBeneficiary: boolean;
  onAddBeneficiaryClicked: () => void;
  onAddBeneficiaryDone: () => void;
};

const BeneficiariesFollowup: FC<BeneficiariesFollowupProps> = ({
  accountHolderEmail,
  beneficiaries,
  isAddingNewBeneficiary,
  onAddBeneficiaryClicked,
  onAddBeneficiaryDone,
}) => {
  const currentBeneficiariesEmails = new Set<string>([accountHolderEmail]);

  beneficiaries.forEach(({ invitation }) => {
    if (invitation?.inviteeEmail) {
      currentBeneficiariesEmails.add(invitation.inviteeEmail);
    }
  });

  if (isAddingNewBeneficiary) {
    return (
      <CreateBeneficiary
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        onBeneficiaryCreated={onAddBeneficiaryDone}
        onCancel={onAddBeneficiaryDone}
      />
    );
  }

  const totalBeneficiaries = beneficiaries.filter(
    (beneficiary) => !beneficiary.isCurrentUser,
  ).length;

  const beneficiariesBySection = beneficiaries.reduce(
    (acc, beneficiary) => {
      if (beneficiary.isCurrentUser) {
        return acc;
      }

      if (beneficiary.invitation && !beneficiary.invitation.inviteeEmail) {
        acc.missingEmail.push(beneficiary);
        return acc;
      }

      switch (beneficiary.onboardingStatus) {
        case CompanyLegalBeneficiaryOnboardingStatus.NotStarted: {
          acc.notStarted.push(beneficiary);
          break;
        }

        case CompanyLegalBeneficiaryOnboardingStatus.InProgress: {
          acc.inProgress.push(beneficiary);
          break;
        }

        case CompanyLegalBeneficiaryOnboardingStatus.InReview:
        case CompanyLegalBeneficiaryOnboardingStatus.Validated: {
          acc.completed.push(beneficiary);
          break;
        }

        case CompanyLegalBeneficiaryOnboardingStatus.Refused:
        case CompanyLegalBeneficiaryOnboardingStatus.Invalid:
        default: {
          acc.error.push(beneficiary);
          break;
        }
      }

      return acc;
    },
    {
      completed: new Array<Beneficiary>(),
      error: new Array<Beneficiary>(),
      inProgress: new Array<Beneficiary>(),
      missingEmail: new Array<Beneficiary>(),
      notStarted: new Array<Beneficiary>(),
    },
  );

  return (
    <Flex direction="column">
      <BeneficiarySection
        beneficiaries={beneficiariesBySection.missingEmail}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        description={locales.sections.missingInfo.description}
        icon="warning"
        title={locales.sections.missingInfo.title}
      />
      <BeneficiarySection
        beneficiaries={beneficiariesBySection.error}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        description={locales.sections.invalid.description}
        icon="warning"
        title={locales.sections.invalid.title}
      />
      <BeneficiarySection
        beneficiaries={beneficiariesBySection.notStarted}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        icon="clock"
        title={locales.sections.notStarted.title}
      />
      <BeneficiarySection
        beneficiaries={beneficiariesBySection.inProgress}
        currentBeneficiariesEmails={currentBeneficiariesEmails}
        icon="edit-file"
        title={locales.sections.inProgress.title}
      />
      <Accordion allowToggle>
        <AccordionItem border={0} marginBottom="space-16">
          <AccordionButton padding={0}>
            <Box alignItems="center" display="flex" flex={1}>
              <SunshineIcon
                color="grey.700"
                marginRight="space-8"
                name="tick"
                size="icon-16"
              />
              <Text>{locales.sections.complete.title}</Text>
            </Box>
            <Box alignItems="center" display="flex">
              <Text>
                {locales.formatString(locales.completedCount, {
                  completed: beneficiariesBySection.completed.length,
                  total: totalBeneficiaries,
                })}
              </Text>
              {beneficiariesBySection.completed.length ? (
                <AccordionIcon
                  color="grey.600"
                  height="24px"
                  marginLeft="space-8"
                  width="24px"
                />
              ) : null}
            </Box>
          </AccordionButton>
          {beneficiariesBySection.completed.length ? (
            <AccordionPanel marginTop="space-16" padding={0}>
              {beneficiariesBySection.completed.map((beneficiary) => (
                <Box
                  key={beneficiary.companyLegalBeneficiaryId}
                  marginBottom="space-8"
                  width="100%"
                >
                  <BeneficiaryCard
                    beneficiary={beneficiary}
                    currentBeneficiariesEmails={currentBeneficiariesEmails}
                  />
                </Box>
              ))}
            </AccordionPanel>
          ) : null}
        </AccordionItem>
      </Accordion>
      {beneficiariesBySection.completed.length < totalBeneficiaries ? (
        <Callout hideIcon marginTop="space-40">
          <Text size="sm">
            {locales.missing}{' '}
            <Link onClick={onAddBeneficiaryClicked} size="sm">
              {locales.add}
            </Link>
          </Text>
        </Callout>
      ) : null}
    </Flex>
  );
};

export default BeneficiariesFollowup;
